import { useQuery } from '@tanstack/react-query';
import type { DashboardSummary } from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

/**
 * RL-377 — the whole dashboard in one round trip.
 *
 * The response shape is `packages/contracts/schemas/domain/dashboard.ts`; the
 * only thing worth repeating here is that every block is OPTIONAL. An absent
 * block means "not yours to see, or not licensed", so callers render what they
 * were given and never probe for a 403. A present block full of zeros means the
 * opposite — asked, answered, nothing there — and must render as zeros.
 */

export const DASHBOARD_SUMMARY_KEY = ['dashboard', 'summary'] as const;

/**
 * `date` is `YYYY-MM-DD` in the tenant's resolved zone; omit it for today. The
 * server resolves the window either way and echoes back which one it used.
 */
export function useDashboardSummary(date?: string) {
  return useQuery({
    queryKey: [...DASHBOARD_SUMMARY_KEY, date ?? 'today'] as const,
    queryFn: async (): Promise<DashboardSummary> => {
      const res = await apiClient.get<DashboardSummary>('/dashboard/summary', {
        params: date === undefined ? {} : { date },
      });
      return res.data;
    },
    /**
     * 30 s, and no faster: this page is left open all day on a shop PC, so the
     * poll is a standing cost on the API rather than a one-off. It matches the
     * interval `useSyncLagBulk` and `useTerminals` already run at.
     */
    refetchInterval: 30_000,
  });
}
